/**
 * Project the 3D roof-mesh modules onto a Google Static Maps photo.
 * Offset is 0: panels share the photo's frame. The title boundary keeps its own correction.
 */

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { GeoJSONFeature } from "@/api/types";
import { API_BASE } from "@/constants/api";
import { preferredSessionMount, visibleLayoutForPackage } from "@/utils/panelLayoutSession";
import { layoutsForMesh, prepareRoofMesh } from "@/utils/roofMeshScene";
import type { MeshGroundFrame, MeshWorldCenter } from "@/utils/satelliteGroundPlane";
import {
  latLngToStaticMapPixel,
  parseStaticMapSize,
  parseStaticMapView,
  staticMapUrlWithoutMarkers,
  type LatLng,
  type Pixel,
} from "@/utils/solarLayoutOverlay";
import type { PlacedRoofPanel, Vec3 } from "@/utils/roofMeshPanelPlacement";

export const PANEL_FILL = "#1a1a1a";
export const PANEL_STROKE = "rgba(255,255,255,0.45)";
/** Keep the quote submit body small enough for the API limit. */
export const AERIAL_DATA_URL_LIMIT = 8_000_000;

export type SatellitePanelSnapshot = {
  satelliteImageUrl: string;
  panels: PlacedRoofPanel[];
  align: MeshWorldCenter;
  groundFrame: MeshGroundFrame;
};

type MapView = {
  center: LatLng;
  zoom: number;
  width: number;
  height: number;
  requested?: { width: number; height: number };
};

/** Inverse of latLngToMeshLocal then meshLocalToWorld. */
export function meshWorldToLatLng(
  world: { x: number; z: number },
  center: MeshWorldCenter,
  frame: MeshGroundFrame,
): LatLng | null {
  const localX = -world.x - center.x;
  const localY = -world.z - center.z;
  const latitude = frame.originLatitude
    + localX * frame.eastLatitudePerM
    + localY * frame.southLatitudePerM;
  const longitude = frame.originLongitude
    + localX * frame.eastLongitudePerM
    + localY * frame.southLongitudePerM;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

/**
 * Plan-view corners of one module, in static-map pixels.
 * Uses the same across × normal axis as the 3D mesh.
 */
export function panelFootprintPixels(
  panel: PlacedRoofPanel,
  align: MeshWorldCenter,
  frame: MeshGroundFrame,
  view: MapView,
): Pixel[] | null {
  const pixels: Pixel[] = [];
  for (const corner of panelCorners(panel)) {
    const latLng = meshWorldToLatLng(corner, align, frame);
    if (!latLng) return null;
    pixels.push(latLngToStaticMapPixel(
      latLng.latitude,
      latLng.longitude,
      view.center,
      view.zoom,
      view.width,
      view.height,
      view.requested,
      0,
    ));
  }
  return pixels;
}

export function drawPanelQuads(ctx: CanvasRenderingContext2D, quads: Pixel[][]) {
  ctx.save();
  ctx.lineJoin = "round";
  ctx.fillStyle = PANEL_FILL;
  ctx.strokeStyle = PANEL_STROKE;
  ctx.lineWidth = 1;
  for (const quad of quads) {
    if (quad.length < 3) continue;
    ctx.beginPath();
    quad.forEach((point, index) => {
      if (index === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    });
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/** Satellite photo plus panel rectangles, as a PNG data URL. Null if the photo cannot be drawn. */
export async function composeSatellitePanelPng(snapshot: SatellitePanelSnapshot): Promise<string | null> {
  try {
    const photoUrl = staticMapUrlWithoutMarkers(snapshot.satelliteImageUrl);
    const view = parseStaticMapView(photoUrl);
    if (!view) return null;
    const textureUrl = `${API_BASE}/api/property/satellite-texture?url=${encodeURIComponent(photoUrl)}`;
    const response = await fetch(textureUrl);
    if (!response.ok) return null;
    const bitmap = await createImageBitmap(await response.blob());
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const requested = parseStaticMapSize(view.size);
    const quads = snapshot.panels.flatMap((panel) => {
      const pixels = panelFootprintPixels(panel, snapshot.align, snapshot.groundFrame, {
        center: view.center,
        zoom: view.zoom,
        width: canvas.width,
        height: canvas.height,
        requested,
      });
      return pixels ? [pixels] : [];
    });
    drawPanelQuads(ctx, quads);
    const dataUrl = canvas.toDataURL("image/png");
    return dataUrl.length <= AERIAL_DATA_URL_LIMIT ? dataUrl : null;
  } catch {
    return null;
  }
}

export async function aerialImageForSubmit(snapshot: SatellitePanelSnapshot | null): Promise<string | undefined> {
  if (!snapshot) return undefined;
  const image = await composeSatellitePanelPng(snapshot);
  return image ?? undefined;
}

export type AerialSubmitContext = {
  mesh3dUrl: string | null;
  meshGroundFrame: MeshGroundFrame | null;
  satelliteImageUrl: string | null;
  propertyBoundary?: GeoJSONFeature | null;
  visiblePanelCount?: number | null;
};

/** Same panel layout the Satellite View tab uses (mesh placement + session edits). */
export async function loadSatellitePanelSnapshot(
  context: AerialSubmitContext,
): Promise<SatellitePanelSnapshot | null> {
  const {
    mesh3dUrl,
    meshGroundFrame,
    satelliteImageUrl,
    propertyBoundary,
    visiblePanelCount,
  } = context;
  const photo = satelliteImageUrl?.trim();
  if (!mesh3dUrl || !meshGroundFrame || !photo) return null;

  return new Promise((resolve) => {
    const loader = new GLTFLoader();
    loader.load(
      mesh3dUrl,
      (gltf) => {
        try {
          const prepared = prepareRoofMesh(gltf.scene, {
            groundFrame: meshGroundFrame,
            propertyBoundary: propertyBoundary ?? null,
            satelliteImageUrl: photo,
            cacheKey: mesh3dUrl,
          });
          const automaticLayout = layoutsForMesh(prepared.cacheKey, gltf.scene).portrait ?? null;
          if (!automaticLayout?.panels.length) {
            resolve(null);
            return;
          }
          const mount = preferredSessionMount(mesh3dUrl);
          const displayed = visibleLayoutForPackage(
            automaticLayout,
            mesh3dUrl,
            mount,
            visiblePanelCount,
          );
          if (!displayed.panels.length) {
            resolve(null);
            return;
          }
          resolve({
            satelliteImageUrl: photo,
            panels: displayed.panels,
            align: { x: prepared.align.x, z: prepared.align.z },
            groundFrame: meshGroundFrame,
          });
        } catch {
          resolve(null);
        }
      },
      undefined,
      () => resolve(null),
    );
  });
}

export async function waitForSatelliteSnapshot(
  getSnapshot: () => SatellitePanelSnapshot | null,
  options?: { timeoutMs?: number; intervalMs?: number },
): Promise<SatellitePanelSnapshot | null> {
  const timeoutMs = options?.timeoutMs ?? 6000;
  const intervalMs = options?.intervalMs ?? 100;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const snap = getSnapshot();
    if (snap?.panels?.length) return snap;
    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
  }
  const last = getSnapshot();
  return last?.panels?.length ? last : null;
}

/**
 * Proposal v2 "Your system" aerial: wait for the live Satellite View snapshot,
 * then build the same composite if the mesh finished loading after the user clicked submit.
 */
export async function resolveAerialImageForSubmit(
  getLiveSnapshot: () => SatellitePanelSnapshot | null,
  context: AerialSubmitContext,
): Promise<string | undefined> {
  let snapshot = await waitForSatelliteSnapshot(getLiveSnapshot, { timeoutMs: 2500 });
  if (!snapshot?.panels?.length) {
    snapshot = await loadSatellitePanelSnapshot(context);
  }
  return aerialImageForSubmit(snapshot);
}

function panelCorners(panel: PlacedRoofPanel): Array<{ x: number; z: number }> {
  const normal = normalize(panel.normal);
  const across = normalize(panel.across);
  const down = cross(across, normal);
  const halfWidth = panel.width / 2;
  const halfHeight = panel.height / 2;
  return [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ].map(([acrossSign, downSign]) => ({
    x: panel.position.x + across.x * halfWidth * acrossSign + down.x * halfHeight * downSign,
    z: panel.position.z + across.z * halfWidth * acrossSign + down.z * halfHeight * downSign,
  }));
}

function normalize(value: Vec3): Vec3 {
  const length = Math.hypot(value.x, value.y, value.z) || 1;
  return { x: value.x / length, y: value.y / length, z: value.z / length };
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}
